import { useEffect, useState } from "react";

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import EmailIcon from '@mui/icons-material/Email';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import { blue, grey } from "@mui/material/colors";

import { withTranslation, Trans } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { isEqual } from "lodash";
import i18next from "@plannel/utils/i18n";

import authService from "@plannel/services/system/auth-service";
import ssoService from "@plannel/services/system/sso-service";
import systemService from "@plannel/services/system/system-service"

import { RouteList } from "@plannel/routeList";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import PlannelFooter from "@plannel/components/PlannelFooter";
import SelectLanguage from "@plannel/components/SelectLanguage"
import BackToSignIn from "@plannel/components/BackToSignIn";
import logo from "@plannel/assets/icons/main-logo.png";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "../utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

const METHOD = { APP: "APP", MAIL: "MAIL" };

const TwoFactorAuthentication = ({ t }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const username = location?.state?.username;
  const tenant = location?.state?.tenant;
  const password = location?.state?.password;
  const ssoType = location?.state?.ssoType;

  const [showList, setShowList] = useState(true);
  const [method, setMethod] = useState(null);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");

  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const { snackBarMessageColor } = ColorUtil;

  const handleResponse = () => {
    if (location.pathname !== RouteList.Signin.path && location.pathname !== RouteList.TwoFactorAuthentication.path && location.pathname !== RouteList.Root.path) {
      navigate(location.pathname);
    } else {
      navigate(RouteList.Home.path);
    }
    window.location.reload();
  }

  const handleErrorResponse = error => {
    const { data } = error.response;
    const errorMessage = data?.message ? data.message : "MSG_NetworkError";

    setMsg(errorMessage);
  }

  const sendOTP = () => {
    systemService.generateQTP(tenant, username)
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        debug(e);
      });
  }

  const verifyCode = authMethod => {
    const params = { username, code, method: authMethod };

    systemService.verifyCode(tenant, params)
      .then(
        () => {
          if (isEqual(ssoType, CodeUtil.loginType.ID_PSWD)) {
            authService.login(tenant, username, password, true).then(
              () => handleResponse(),
              (error) => handleErrorResponse(error)
            );
          } else {
            const ssoParams = { email: username, passedMfa: true };
            ssoService.googleSignInSSO(tenant, ssoParams).then(
              () => handleResponse(),
              (error) => handleErrorResponse(error)
            );
          }
        },
        (error) => {
          const { message } = error.response.data;
          setMsg(message);
          debug(error);
        }
      );
  }

  const handleDialog = (action) => {
    if (action === "RESEND") {
      sendOTP();
    }
  }

  const setErrorMessage = () => {
    if (i18next.exists(msg)) {
      return t(msg);
    }

    return t("MSG_NetworkError");
  }

  const changeAuthMethod = methodName => {
    setMethod(methodName);
    setShowList(false);
    setCode("");
  }

  useEffect(() => {
    if (isEqual(method, METHOD.MAIL)) {
      sendOTP();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  useEffect(() => {
    if (!location?.state) {
      navigate(RouteList.Signin.path);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listItemStyle = { color: blue.A400, cursor: 'pointer', my: 0.5, fontSize: FS.H1 };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }} >
        <Box mt={8}>
          <img src={logo} width={170} alt="loading..." />
        </Box>
        <Typography variant="h5" my={1}>{t("twoFactorAuth")}</Typography>
        {
          showList ? (
            <>
              <Box mt={2} />
              <PrivacyTipIcon />
              <Typography variant="h6" fontSize={FS.DISPLAY} my={1}>{t("selectAuthMethod")}</Typography>
              <AppCard sx={{ m: 1, width: "100%" }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography sx={listItemStyle} key={METHOD.MAIL} onClick={() => changeAuthMethod(METHOD.MAIL)} gutterBottom>{t("loginWithEmail")}</Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={listItemStyle} key={METHOD.APP} onClick={() => changeAuthMethod(METHOD.APP)} gutterBottom>{t("loginWithAuthApp")}</Typography>
                </CardContent>
              </AppCard>
            </>
          ) :
            (
              isEqual(method, METHOD.MAIL) ? (
                <AppCard>
                  <CardContent sx={{ pb: '16px !important', display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <EmailIcon />
                    <Typography variant="h6" fontSize={FS.DISPLAY} my={1}>{`${t("email")} OTP`}</Typography>
                    <TextField
                      fullWidth
                      required
                      autoComplete="one-time-code"
                      margin="normal"
                      size="small"
                      placeholder={"XXXXXX"}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                    <Button fullWidth sx={{ my: 2 }} variant="contained" onClick={() => verifyCode(METHOD.MAIL)}>{t("verify")}</Button>
                    <>
                      <Typography variant="body2" sx={{ my: 1, mx: 0.5 }}>{t("MSG_PleaseCheckEmail")}</Typography>
                      <Typography variant="body2" sx={{ mb: 2, mx: 0.5, mr: "auto", fontSize: FS.TITLE, color: grey[500] }}>
                        <Trans
                          i18nKey="MSG_DidNotReceiveOPT"
                          components={[
                            <Box key="resend" component={"span"} sx={{ textDecoration: 'underline', cursor: 'pointer', fontStyle: 'italic' }} onClick={() => setDialogInfo({ open: true, title: "resend", content: t("MSG_ConfirmResend") })}></Box>
                          ]}
                        />
                      </Typography>
                    </>
                    <Typography sx={{ mr: "auto", color: blue.A400, cursor: 'pointer', my: 1, fontSize: FS.TITLE }} onClick={() => setShowList(true)}>{t("tryAnotherWay")}</Typography>
                  </CardContent>
                </AppCard>
              ) : (
                <AppCard>
                  <CardContent sx={{ pb: '16px !important', display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <SmartphoneIcon />
                    <Typography variant="h6" fontSize={FS.DISPLAY} my={1}>{t("authAppCode")}</Typography>
                    <TextField
                      fullWidth
                      required
                      autoComplete="one-time-code"
                      margin="normal"
                      size="small"
                      placeholder={"XXXXXX"}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                    <Button fullWidth sx={{ my: 2 }} variant="contained" onClick={() => verifyCode(METHOD.APP)}>{t("verify")}</Button>
                    <Typography variant="body2" sx={{ my: 1, mx: 0.5 }}>{t("MSG_OpenTwoFactorAuthApp")}</Typography>
                    <Typography sx={{ mr: "auto", color: blue.A400, cursor: 'pointer', my: 1, fontSize: FS.TITLE }} onClick={() => setShowList(true)}>{t("tryAnotherWay")}</Typography>
                  </CardContent>
                </AppCard>
              )
            )
        }
      </Box>
      <Box sx={{ mt: 1 }}>
        {Boolean(msg) && (
          <Alert sx={{ mb: 2 }} severity="error">{setErrorMessage()}</Alert>
        )}
        <Box sx={{ mt: 1, display: "flex" }}>
          <SelectLanguage />
          <BackToSignIn />
        </Box>
      </Box>
      <PlannelFooter/>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Container>
  );
};

export default withTranslation()(TwoFactorAuthentication);